import React, { FC, useCallback, useMemo } from 'react';
import { FormInputDropdown } from 'components/form-inputs/form-input-dropdown/FormInputDropdown';
import { FormInputPickerMobile } from 'components/form-inputs/form-input-picker-mobile/FormInputPickerMobile';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { getSortingOptions } from 'src/pages-all/search/components/Sorting/sortingOptions';
import { Field, getFormValues, reduxForm, WrappedFieldProps } from 'redux-form';
import {
  SEARCH_FORM_NAME,
  SearchFormFields,
  SearchFormValues,
} from 'components/search/search.logic';
import { Text } from 'components/text/Text';
import { translate, translationKeys } from 'logic/translations/translations.service';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { offerSlice } from 'logic/store/offer/offer.slice';
import { getValueTranslation } from 'src/pages-all/search/components/Sorting/getValueTranslation';
import { SortOffersBy } from 'logic/api-models/sort-offer-by';
import { TutoringKind } from 'components/search/search.const';

const InputWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 32px 0 24px 0;
`;

export type Data = {
  value: any;
  label: string;
};

export const SortingInput: FC<WrappedFieldProps> = (props) => {
  const { minTablet } = useBreakpoints();
  const dispatch = useDispatch();
  const formValues: SearchFormValues = useSelector(getFormValues(SEARCH_FORM_NAME)) || {};
  const isOnlineSearch = formValues.kind === TutoringKind.onlineTutoring;
  const isOnsiteSearch = formValues.kind === TutoringKind.onSiteTutoring;
  const isCitySelected = !!formValues.city;
  const shouldSortByDistance = !isOnlineSearch && isCitySelected;

  const handleChangeOfSort = useCallback(
    (sortBy?: SortOffersBy) => {
      dispatch(
        offerSlice.actions.navigateToSearchResults({
          formValues: { ...formValues, sortBy, page: 1 },
        })
      );
    },
    [dispatch, formValues]
  );

  const data: Data[] = useMemo(
    () =>
      getSortingOptions(shouldSortByDistance).map((option) => ({
        ...option,
        onChange: handleChangeOfSort,
      })),
    [shouldSortByDistance]
  );

  const Picker = () =>
    minTablet ? (
      <FormInputDropdown {...props} data={data} customOnChange={handleChangeOfSort}>
        <Text
          underline
          color={'primary'}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          fontWeight={500}
        >
          {getValueTranslation(formValues.sortBy)}
        </Text>
      </FormInputDropdown>
    ) : (
      <FormInputPickerMobile {...props} data={data} customOnChange={handleChangeOfSort}>
        <Text
          underline
          color={'primary'}
          style={{ cursor: 'pointer', userSelect: 'none' }}
          fontWeight={500}
        >
          {getValueTranslation(formValues.sortBy)}
        </Text>
      </FormInputPickerMobile>
    );

  return (
    <InputWrapper>
      <div>
        <Text>{translate(translationKeys.search.sortDropdown.sortWith)}</Text>
      </div>
      {': '}
      <Text
        color={'primary'}
        underline={false}
        fontWeight={600}
        style={{ fontFamily: 'open-sans', userSelect: 'none' }}
      >
        ↑↓
      </Text>
      <Picker />
      <br/>
      <div style={{ width: '100%' }}>
        {isOnsiteSearch && <Text>{translate(translationKeys.search.sortDropdown.hint)}</Text>}
      </div>
    </InputWrapper>
  );
};

export const SortingField = reduxForm({
  form: SEARCH_FORM_NAME,
  destroyOnUnmount: false,
})(() => <Field name={SearchFormFields.sortBy} component={SortingInput} />);
